import {
  collection, doc, addDoc, setDoc, updateDoc, deleteDoc,
  onSnapshot, query, orderBy, where, serverTimestamp, getDocs, writeBatch,
  runTransaction, getDoc,
} from 'firebase/firestore';
import { db } from './config';

// ─────────────────────────────────────────────────────────────────────────────
// MULTI-TENANT ARCHITECTURE
// All collections are flat but every document carries a `companyId` field.
// Queries always filter by `companyId` so companies never see each other's data.
// Firestore rules enforce this at the database level via custom claims.
// ─────────────────────────────────────────────────────────────────────────────

// ─── COLLECTION REFS ─────────────────────────────────────────────────────────
export const leadsCol      = () => collection(db, 'leads');
export const followupsCol  = () => collection(db, 'followups');
export const stagesCol     = () => collection(db, 'stages');
export const fieldsCol     = () => collection(db, 'customFields');
export const campaignsCol  = () => collection(db, 'campaigns');
export const usersCol      = () => collection(db, 'users');

// ─── LEADS ────────────────────────────────────────────────────────────────────
export const subscribleLeads = (companyId, callback, onError = null) => {
  const q = query(leadsCol(), where('companyId', '==', companyId), orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    err  => { console.error('[leads]', err.code, err.message); if (onError) onError(err); }
  );
};

export const addLead = (companyId, data) =>
  addDoc(leadsCol(), { ...data, companyId, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });

export const updateLead = (id, data) =>
  updateDoc(doc(db, 'leads', id), { ...data, updatedAt: serverTimestamp() });

export const deleteLead = (id) => deleteDoc(doc(db, 'leads', id));

// Batch-delete up to N leads — avoids hitting Firestore/browser limits
export const bulkDeleteLeads = async (ids) => {
  const CHUNK = 500; // Firestore batch max
  for (let i = 0; i < ids.length; i += CHUNK) {
    const batch = writeBatch(db);
    ids.slice(i, i + CHUNK).forEach(id => batch.delete(doc(db, 'leads', id)));
    await batch.commit();
  }
};

// ─── FOLLOWUPS ────────────────────────────────────────────────────────────────
export const subscribeFollowups = (companyId, callback, onError = null) => {
  const q = query(followupsCol(), where('companyId', '==', companyId), orderBy('dueDate', 'asc'));
  return onSnapshot(
    q,
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    err  => { console.error('[followups]', err.code, err.message); if (onError) onError(err); }
  );
};

export const addFollowup = (companyId, data) =>
  addDoc(followupsCol(), { ...data, companyId, createdAt: serverTimestamp() });

export const updateFollowup = (id, data) =>
  updateDoc(doc(db, 'followups', id), data);

export const deleteFollowup = (id) => deleteDoc(doc(db, 'followups', id));

// ─── PIPELINE STAGES ─────────────────────────────────────────────────────────
export const subscribeStages = (companyId, callback, onError = null) => {
  const q = query(stagesCol(), where('companyId', '==', companyId), orderBy('order', 'asc'));
  return onSnapshot(
    q,
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    err  => { console.error('[stages]', err.code, err.message); if (onError) onError(err); }
  );
};

export const addStage = (companyId, data) => addDoc(stagesCol(), { ...data, companyId });

export const updateStage = (id, data) => updateDoc(doc(db, 'stages', id), data);

export const deleteStage = (id) => deleteDoc(doc(db, 'stages', id));

// ─── CUSTOM FIELDS ────────────────────────────────────────────────────────────
export const subscribeCustomFields = (companyId, callback, onError = null) => {
  const q = query(fieldsCol(), where('companyId', '==', companyId));
  return onSnapshot(
    q,
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    err  => { console.error('[customFields]', err.code, err.message); if (onError) onError(err); }
  );
};

export const addCustomField = (companyId, data) => addDoc(fieldsCol(), { ...data, companyId });

export const deleteCustomField = (id) => deleteDoc(doc(db, 'customFields', id));

// ─── CAMPAIGNS ────────────────────────────────────────────────────────────────
export const subscribeCampaigns = (companyId, callback, onError = null) => {
  const q = query(campaignsCol(), where('companyId', '==', companyId), orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    err  => { console.error('[campaigns]', err.code, err.message); if (onError) onError(err); }
  );
};

export const addCampaign = (companyId, data) =>
  addDoc(campaignsCol(), { ...data, companyId, createdAt: serverTimestamp() });

export const updateCampaign = (id, data) => updateDoc(doc(db, 'campaigns', id), data);

// ─── SETTINGS (per-company subcollection) ────────────────────────────────────
// Path: companies/{companyId}/settings/{docId}
const companySettingsDoc = (companyId, docId) =>
  doc(db, 'companies', companyId, 'settings', docId);

export const subscribeIntegrations = (companyId, callback, onError = null) =>
  onSnapshot(
    companySettingsDoc(companyId, 'integrations'),
    snap => callback(snap.exists() ? snap.data() : {}),
    err  => { console.error('[integrations]', err.code, err.message); if (onError) onError(err); }
  );

export const saveIntegrations = (companyId, data) =>
  setDoc(companySettingsDoc(companyId, 'integrations'), data, { merge: true });

export const subscribeCustomSources = (companyId, callback) =>
  onSnapshot(companySettingsDoc(companyId, 'sources'), snap =>
    callback(snap.exists() ? (snap.data().sources || []) : [])
  );

export const saveCustomSources = (companyId, sources) =>
  setDoc(companySettingsDoc(companyId, 'sources'), { sources }, { merge: false });

export const subscribeRRSettings = (companyId, callback) =>
  onSnapshot(companySettingsDoc(companyId, 'rrSettings'), snap =>
    callback(snap.exists() ? snap.data() : { mode: 'round_robin', rrPointer: 0, queue: [], logVisibility: [] })
  );

export const saveRRSettings = (companyId, data) =>
  setDoc(companySettingsDoc(companyId, 'rrSettings'), data, { merge: true });

// ─── USERS ────────────────────────────────────────────────────────────────────
export const subscribeUsers = (companyId, callback, onError = null) => {
  const q = query(usersCol(), where('companyId', '==', companyId));
  return onSnapshot(
    q,
    // Filter out superadmin accounts — they are platform-level users and must
    // never appear in a company's team list, preventing admins from accidentally
    // removing them and locking out the entire platform.
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => u.role !== 'superadmin')),
    err  => { console.error('[users]', err.code, err.message); if (onError) onError(err); }
  );
};

export const addUserProfile = (uid, data) =>
  setDoc(doc(db, 'users', uid), { ...data, createdAt: serverTimestamp() });

export const updateUserProfile = (uid, data) =>
  updateDoc(doc(db, 'users', uid), data);

export const deleteUserProfile = (uid) => deleteDoc(doc(db, 'users', uid));

// Normalize the same way everywhere an email is stored/queried, so a
// pre-registration invite (typed by an admin) always matches the same
// person's email later (typed by them at signup), regardless of casing
// or stray whitespace. Without this, an exact-match Firestore query finds
// zero docs on a casing mismatch — not a permissions error, just silently
// no match — and the new user ends up with no companyId at all.
export const normalizeEmail = (email) => (email || '').trim().toLowerCase();

export const addTeamMember = (companyId, data) =>
  addDoc(usersCol(), { ...data, email: normalizeEmail(data.email), companyId, createdAt: serverTimestamp() });

// Soft-delete: mark removed so AuthContext auto-signs them out in real-time.
// Hard delete caused a race where the profile re-created itself with companyId=null
// before the user was kicked; this is instant and leaves an audit record.
// Superadmins are platform-level and cannot be removed via company-level actions.
export const deleteTeamMember = async (id) => {
  const snap = await getDoc(doc(db, 'users', id));
  if (snap.exists() && snap.data()?.role === 'superadmin') {
    throw new Error('Cannot remove a super admin account from a company.');
  }
  return updateDoc(doc(db, 'users', id), {
    status: 'removed',
    companyId: null,
    removedAt: serverTimestamp(),
  });
};

export const queryUserByEmail = async (email) => {
  const normalized = normalizeEmail(email);
  if (!normalized) return null;
  const q = query(usersCol(), where('email', '==', normalized));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
};

// ─── LEAD NOTES ──────────────────────────────────────────────────────────────
export const notesCol = () => collection(db, 'leadNotes');

export const subscribeLeadNotes = (leadId, companyId, callback) => {
  const constraints = [where('leadId', '==', leadId)];
  if (companyId) constraints.push(where('companyId', '==', companyId));
  constraints.push(orderBy('createdAt', 'desc'));
  const q = query(notesCol(), ...constraints);
  return onSnapshot(q,
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    err => console.error('[LeadNotes]', err.code, err.message),
  );
};

export const addLeadNote = (companyId, data) =>
  addDoc(notesCol(), { ...data, companyId, createdAt: serverTimestamp() });

export const deleteLeadNote = (id) => deleteDoc(doc(db, 'leadNotes', id));

// One realtime subscription for every note belonging to a company, instead of
// one subscription per lead. Used by the Digital Media page, which needs to
// show/export notes across many leads at once rather than one lead at a time.
export const subscribeCompanyLeadNotes = (companyId, callback, onError = null) => {
  const q = query(notesCol(), where('companyId', '==', companyId), orderBy('createdAt', 'desc'));
  return onSnapshot(q, snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))), onError || (() => {}));
};

// ─── WEB FORMS ────────────────────────────────────────────────────────────────
export const webFormsCol = () => collection(db, 'webForms');

export const subscribeWebForms = (companyId, callback) => {
  // No orderBy — avoids composite index requirement; sort by createdAt in JS
  const q = query(webFormsCol(), where('companyId', '==', companyId));
  return onSnapshot(
    q,
    snap => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort newest first client-side
      docs.sort((a, b) => {
        const at = a.createdAt?.toMillis?.() ?? a.createdAt?.seconds ?? 0;
        const bt = b.createdAt?.toMillis?.() ?? b.createdAt?.seconds ?? 0;
        return bt - at;
      });
      callback(docs);
    },
    err => { console.error('[webForms]', err.code, err.message); callback([]); }
  );
};

export const addWebForm = (companyId, data) =>
  addDoc(webFormsCol(), { ...data, companyId, createdAt: serverTimestamp() });

export const updateWebForm = (id, data) => updateDoc(doc(db, 'webForms', id), data);

export const deleteWebForm = (id) => deleteDoc(doc(db, 'webForms', id));

export const getWebForm = (id) => getDoc(doc(db, 'webForms', id));

// ─── ASSESSMENTS ──────────────────────────────────────────────────────────────
export const assessmentsCol = () => collection(db, 'assessments');

export const addAssessment = (companyId, data) =>
  addDoc(assessmentsCol(), { ...data, companyId, createdAt: serverTimestamp(), status: 'pending' });

export const updateAssessment = (id, data) => updateDoc(doc(db, 'assessments', id), data);

export const getAssessment = (id) => getDoc(doc(db, 'assessments', id));

export const subscribeAssessmentsForLead = (leadId, callback) => {
  const q = query(assessmentsCol(), where('leadId', '==', leadId));
  return onSnapshot(q, snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
};

// ─── ACTIVITY LOGS ────────────────────────────────────────────────────────────
export const activityLogsCol = () => collection(db, 'activityLogs');

export const addActivityLog = (companyId, data) =>
  addDoc(activityLogsCol(), { ...data, companyId, timestamp: serverTimestamp() });

export const subscribeActivityLogs = (companyId, callback, onError = null) => {
  const q = query(activityLogsCol(), where('companyId', '==', companyId), orderBy('timestamp', 'desc'));
  return onSnapshot(
    q,
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    err => { console.error('[activityLogs]', err); if (onError) onError(err); }
  );
};

export const subscribeUserActivityLogs = (companyId, userId, callback) => {
  const q = query(
    activityLogsCol(),
    where('companyId', '==', companyId),
    where('userId', '==', userId),
    orderBy('timestamp', 'desc')
  );
  return onSnapshot(q, snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
};

// ─── COMPANIES (multi-tenant) ─────────────────────────────────────────────────
export const companiesCol = () => collection(db, 'companies');

export const subscribeCompanies = (callback) =>
  onSnapshot(companiesCol(), snap =>
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );

export const subscribeCompany = (id, callback) =>
  onSnapshot(doc(db, 'companies', id), snap =>
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null)
  );

export const upsertCompany = (id, data) =>
  setDoc(doc(db, 'companies', id), { ...data }, { merge: true });

export const updateCompany = (id, data) =>
  updateDoc(doc(db, 'companies', id), data);

export const deleteCompany = (id) => deleteDoc(doc(db, 'companies', id));

export const ensureCompany = async (companyId, initialData = {}) => {
  const ref = doc(db, 'companies', companyId);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      name:     initialData.name     || 'My Company',
      slug:     companyId,
      logoUrl:  initialData.logoUrl  || '',
      theme: {
        appName:   initialData.appName   || 'Clarion CRM',
        primary:   initialData.primary   || '#7c3aed',
        secondary: initialData.secondary || '#3b82f6',
      },
      features: {
        webForms: true, assessments: true, campaigns: true,
        integrations: true, activityLog: true, roundRobin: true,
      },
      plan:      'pro',
      active:    true,
      createdAt: serverTimestamp(),
    });
    // Seed default stages + fields for this new company
    await seedCompanyDefaults(companyId);
  }
};

// ─── DEFAULT DATA SEED (per-company) ─────────────────────────────────────────
export const DEFAULT_PIPELINE_STAGES = [
  { name: 'New Enquiry',      color: '#7c3aed', order: 0 },
  { name: 'Contacted',        color: '#3b82f6', order: 1 },
  { name: 'Docs Collecting',  color: '#f59e0b', order: 2 },
  { name: 'Application Sent', color: '#06b6d4', order: 3 },
  { name: 'Offer Received',   color: '#10b981', order: 4 },
  { name: 'Visa Applied',     color: '#8b5cf6', order: 5 },
  { name: 'Enrolled',         color: '#22c55e', order: 6 },
];

export const DEFAULT_CUSTOM_FIELDS = [
  { label: 'Course / Program',   type: 'text',     required: false },
  { label: 'Target Country',     type: 'select',   required: false, options: ['UK','Canada','Australia','USA','Germany','Ireland','New Zealand','Singapore','France','Netherlands'] },
  { label: 'Preferred Intake',   type: 'select',   required: false, options: ['Jan 2025','May 2025','Sep 2025','Jan 2026','May 2026','Sep 2026','Jan 2027','May 2027','Sep 2027'] },
  { label: 'Budget (USD)',       type: 'number',   required: false },
  { label: 'Qualification',      type: 'select',   required: false, options: ['10th','12th','Diploma','Undergraduate','Postgraduate','PhD'] },
  { label: 'IELTS Score',        type: 'number',   required: false },
  { label: 'TOEFL Score',        type: 'number',   required: false },
  { label: 'PTE Score',          type: 'number',   required: false },
  { label: 'Passport Number',    type: 'text',     required: false },
  { label: 'Visa Applied',       type: 'checkbox', required: false },
];

export const seedCompanyDefaults = async (companyId) => {
  const [stageSnap, fieldSnap] = await Promise.all([
    getDocs(query(stagesCol(), where('companyId', '==', companyId))),
    getDocs(query(fieldsCol(), where('companyId', '==', companyId))),
  ]);
  if (stageSnap.size > 0 && fieldSnap.size > 0) return; // already seeded
  const batch = writeBatch(db);
  if (stageSnap.size === 0) {
    DEFAULT_PIPELINE_STAGES.forEach(s => batch.set(doc(stagesCol()), { ...s, companyId }));
  }
  if (fieldSnap.size === 0) {
    DEFAULT_CUSTOM_FIELDS.forEach(f => batch.set(doc(fieldsCol()), { ...f, companyId }));
  }
  await batch.commit();
};

// Legacy seed (used for existing 'default' company migration)
export const seedDefaultData = async (companyId = 'default') => {
  await ensureCompany(companyId);
};

export const restoreDefaultData = async (companyId) => {
  const [stageSnap, fieldSnap] = await Promise.all([
    getDocs(query(stagesCol(), where('companyId', '==', companyId))),
    getDocs(query(fieldsCol(), where('companyId', '==', companyId))),
  ]);
  const existingStageNames  = new Set(stageSnap.docs.map(d => (d.data().name  || '').toLowerCase().trim()));
  const existingFieldLabels = new Set(fieldSnap.docs.map(d => (d.data().label || '').toLowerCase().trim()));
  const batch = writeBatch(db);
  let stagesAdded = 0, fieldsAdded = 0;
  DEFAULT_PIPELINE_STAGES.forEach(s => {
    if (!existingStageNames.has(s.name.toLowerCase())) { batch.set(doc(stagesCol()), { ...s, companyId }); stagesAdded++; }
  });
  DEFAULT_CUSTOM_FIELDS.forEach(f => {
    if (!existingFieldLabels.has(f.label.toLowerCase())) { batch.set(doc(fieldsCol()), { ...f, companyId }); fieldsAdded++; }
  });
  if (stagesAdded > 0 || fieldsAdded > 0) await batch.commit();
  return { stagesAdded, fieldsAdded };
};

export const deduplicateCollections = async (companyId) => {
  const [stageSnap, fieldSnap] = await Promise.all([
    getDocs(query(stagesCol(), where('companyId', '==', companyId))),
    getDocs(query(fieldsCol(), where('companyId', '==', companyId))),
  ]);
  const seenStages = new Set(), dupStageIds = [];
  stageSnap.docs.forEach(d => {
    const key = (d.data().name || '').toLowerCase().trim();
    if (seenStages.has(key)) dupStageIds.push(d.id); else seenStages.add(key);
  });
  const seenFields = new Set(), dupFieldIds = [];
  fieldSnap.docs.forEach(d => {
    const key = (d.data().label || '').toLowerCase().trim();
    if (seenFields.has(key)) dupFieldIds.push(d.id); else seenFields.add(key);
  });
  if (!dupStageIds.length && !dupFieldIds.length) return { stagesRemoved: 0, fieldsRemoved: 0 };
  const batch = writeBatch(db);
  dupStageIds.forEach(id => batch.delete(doc(db, 'stages', id)));
  dupFieldIds.forEach(id => batch.delete(doc(db, 'customFields', id)));
  await batch.commit();
  return { stagesRemoved: dupStageIds.length, fieldsRemoved: dupFieldIds.length };
};

// ─── WHATSAPP SESSIONS ────────────────────────────────────────────────────────
export const waSessionsCol = () => collection(db, 'waSessions');

export const subscribeWASession = (counselorId, callback) =>
  onSnapshot(doc(db, 'waSessions', counselorId), snap =>
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null)
  );

export const subscribeAllWASessions = (companyId, callback) => {
  const q = query(waSessionsCol(), where('companyId', '==', companyId));
  return onSnapshot(q, snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
};

// ─── WHATSAPP MESSAGES ────────────────────────────────────────────────────────
export const waMessagesCol = () => collection(db, 'waMessages');

export const subscribeWAMessages = (leadId, callback) => {
  // No orderBy — sort in JS to avoid needing a composite index
  const q = query(waMessagesCol(), where('leadId', '==', leadId));
  return onSnapshot(
    q,
    snap => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => (a.timestamp?.seconds || 0) - (b.timestamp?.seconds || 0));
      callback(docs);
    },
    err => { console.error('[waMessages]', err.code); callback([]); }
  );
};

export const subscribeAllWAMessages = (companyId, callback) => {
  // No orderBy — sort in JS to avoid needing a composite index
  const q = query(waMessagesCol(), where('companyId', '==', companyId));
  return onSnapshot(
    q,
    snap => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));
      callback(docs);
    },
    err => { console.error('[allWAMessages]', err.code); callback([]); }
  );
};

export const markWAMessagesRead = async (leadId, counselorId) => {
  const q = query(
    waMessagesCol(),
    where('leadId', '==', leadId),
    where('fromMe', '==', false),
    where('read', '==', false)
  );
  const snap = await getDocs(q);
  if (snap.empty) return;
  const batch = writeBatch(db);
  snap.docs.forEach(d => batch.update(d.ref, { read: true }));
  await batch.commit();
};

// ─── WHATSAPP TEMPLATES ───────────────────────────────────────────────────────
export const waTemplatesCol = () => collection(db, 'waTemplates');

export const subscribeWATemplates = (companyId, callback) => {
  const q = query(waTemplatesCol(), where('companyId', '==', companyId), orderBy('createdAt', 'asc'));
  return onSnapshot(q, snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
};

export const addWATemplate = (companyId, data) =>
  addDoc(waTemplatesCol(), { ...data, companyId, createdAt: serverTimestamp() });

export const updateWATemplate = (id, data) => updateDoc(doc(db, 'waTemplates', id), data);

export const deleteWATemplate = (id) => deleteDoc(doc(db, 'waTemplates', id));

// ─── WORK MONITORING (pages/WorkReport.js) ───────────────────────────────────
// All four collections live under companies/{companyId}/... per firestore.rules.
// connectedAccounts (OAuth tokens) is intentionally NOT exposed here at all —
// it's locked to `allow read, write: if false`, reachable only by the backend
// Admin SDK (see backend/src/routes/oauth.js). The frontend only ever talks to
// it indirectly, via the backend's /api/oauth/* HTTP endpoints.

// syncedMeetings — written by the backend sync job only; client reads.
export const syncedMeetingsCol = (companyId) =>
  collection(db, 'companies', companyId, 'syncedMeetings');

export const subscribeSyncedMeetings = (companyId, callback, onError = null) => {
  if (!companyId) return () => {};
  return onSnapshot(
    syncedMeetingsCol(companyId),
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    err  => { console.error('[syncedMeetings]', err.code, err.message); if (onError) onError(err); else callback([]); }
  );
};

// Convenience wrapper for the "My Activity" tab — same data, filtered to one
// user. No composite-index `where` + `orderBy` needed since we just filter
// client-side (this collection is small per company).
export const subscribeUserSyncedMeetings = (companyId, userId, callback) =>
  subscribeSyncedMeetings(companyId, all => callback(all.filter(m => m.userId === userId)));

// emailActivity — daily sent/received COUNTS only (never content), backend-written.
export const emailActivityCol = (companyId) =>
  collection(db, 'companies', companyId, 'emailActivity');

export const subscribeEmailActivity = (companyId, callback, onError = null) => {
  if (!companyId) return () => {};
  return onSnapshot(
    emailActivityCol(companyId),
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    err  => { console.error('[emailActivity]', err.code, err.message); if (onError) onError(err); else callback([]); }
  );
};

// workLogs — self-logged "what I worked on" entries. Full client read/write
// (within the same company), unlike the two backend-only collections above.
export const workLogsCol = (companyId) =>
  collection(db, 'companies', companyId, 'workLogs');

export const subscribeWorkLogs = (companyId, callback, onError = null) => {
  if (!companyId) return () => {};
  const q = query(workLogsCol(companyId), orderBy('date', 'desc'));
  return onSnapshot(
    q,
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    err  => { console.error('[workLogs]', err.code, err.message); if (onError) onError(err); else callback([]); }
  );
};

export const subscribeUserWorkLogs = (companyId, userId, callback) =>
  subscribeWorkLogs(companyId, all => callback(all.filter(l => l.userId === userId)));

export const addWorkLog = (companyId, userId, data) =>
  addDoc(workLogsCol(companyId), { ...data, companyId, userId, createdAt: serverTimestamp() });

export const updateWorkLog = (companyId, logId, data) =>
  updateDoc(doc(db, 'companies', companyId, 'workLogs', logId), data);

export const deleteWorkLog = (companyId, logId) =>
  deleteDoc(doc(db, 'companies', companyId, 'workLogs', logId));

// workTargets — admin-set productivity targets. Doc id = userId for a
// per-employee target, or the literal 'default' doc for a company-wide
// fallback target applied to anyone without their own override.
export const workTargetsCol = (companyId) =>
  collection(db, 'companies', companyId, 'workTargets');

export const subscribeWorkTargets = (companyId, callback, onError = null) => {
  if (!companyId) return () => {};
  return onSnapshot(
    workTargetsCol(companyId),
    snap => {
      const map = {};
      snap.docs.forEach(d => { map[d.id] = d.data(); });
      callback(map);
    },
    err => { console.error('[workTargets]', err.code, err.message); if (onError) onError(err); else callback({}); }
  );
};

export const saveWorkTarget = (companyId, targetId, data) =>
  setDoc(doc(db, 'companies', companyId, 'workTargets', targetId), data, { merge: true });

// dailyReportRemarks — per-employee-per-day feedback/clarification thread
// between admin and employee on the Work Report page's Daily Report view.
// Thread doc id is `${userId}_${date}` (date = 'YYYY-MM-DD'); the actual
// messages live in a `messages` subcollection underneath it, same shape as
// the leaveRequests discussion thread. The thread doc itself only carries
// lightweight metadata (last message preview + per-side "unread" flags) so
// the report views can show a reply badge without subscribing to every
// thread's full message history.
export const remarkThreadId = (userId, date) => `${userId}_${date}`;

export const dailyReportRemarksCol = (companyId) =>
  collection(db, 'companies', companyId, 'dailyReportRemarks');

// All thread metadata docs for a company in one shot — lets the admin Team
// Report show "X employees have unread replies" without per-thread reads.
export const subscribeAllRemarkThreadsMeta = (companyId, callback, onError = null) => {
  if (!companyId) return () => {};
  return onSnapshot(
    dailyReportRemarksCol(companyId),
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    err  => { console.error('[dailyReportRemarks]', err.code, err.message); if (onError) onError(err); else callback([]); }
  );
};

export const subscribeRemarkThreadMeta = (companyId, userId, date, callback) => {
  if (!companyId || !userId || !date) return () => {};
  return onSnapshot(
    doc(db, 'companies', companyId, 'dailyReportRemarks', remarkThreadId(userId, date)),
    snap => callback(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    err  => { console.error('[dailyReportRemarks]', err.code, err.message); callback(null); }
  );
};

export const subscribeRemarkMessages = (companyId, userId, date, callback) => {
  if (!companyId || !userId || !date) return () => {};
  const col = collection(db, 'companies', companyId, 'dailyReportRemarks', remarkThreadId(userId, date), 'messages');
  const q = query(col, orderBy('createdAt', 'asc'));
  return onSnapshot(
    q,
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    err  => { console.error('[dailyReportRemarks/messages]', err.code, err.message); callback([]); }
  );
};

// fromRole is 'admin' | 'employee' — drives both the chat-bubble styling and
// which side's "unread" flag gets set on the parent thread doc.
export const addRemarkMessage = async (companyId, userId, date, { fromUserId, fromName, fromRole, text }) => {
  const threadRef = doc(db, 'companies', companyId, 'dailyReportRemarks', remarkThreadId(userId, date));
  await addDoc(collection(threadRef, 'messages'), {
    fromUserId, fromName, fromRole, text, createdAt: serverTimestamp(),
  });
  await setDoc(threadRef, {
    companyId, userId, date,
    lastMessageAt: serverTimestamp(),
    lastMessageText: text,
    lastMessageFromRole: fromRole,
    ...(fromRole === 'admin' ? { employeeUnread: true, adminUnread: false } : { adminUnread: true, employeeUnread: false }),
  }, { merge: true });
};

export const markRemarkThreadSeen = (companyId, userId, date, viewerRole) => {
  if (!companyId || !userId || !date) return Promise.resolve();
  return setDoc(
    doc(db, 'companies', companyId, 'dailyReportRemarks', remarkThreadId(userId, date)),
    { [viewerRole === 'admin' ? 'adminUnread' : 'employeeUnread']: false },
    { merge: true }
  );
};

// ─── Leave Requests — centralized listener + notification flags ──────────────
// Subscribed once in CRMContext (not per-page) so the Header notification
// bell can show "pending application" / "unread employee message" counts no
// matter which page the admin is currently on — previously this lived only
// as a local listener inside Leaves.js, invisible everywhere else in the app.
export const subscribeLeaveRequests = (companyId, callback, onError = null) => {
  if (!companyId) return () => {};
  return onSnapshot(
    collection(db, 'companies', companyId, 'leaveRequests'),
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    err  => { console.error('[leaveRequests]', err.code, err.message); if (onError) onError(err); else callback([]); }
  );
};

// Posts a message into a leave request's discussion thread AND stamps an
// unread flag onto the leaveRequests doc itself (the request doc doubles as
// the thread's lightweight metadata — same idea as dailyReportRemarks above,
// just without a separate metadata doc since the request doc already exists).
// fromRole is 'admin' | 'employee'.
export const addLeaveMessage = async (companyId, requestId, { fromUserId, fromName, fromRole, text }) => {
  const reqRef = doc(db, 'companies', companyId, 'leaveRequests', requestId);
  await addDoc(collection(reqRef, 'messages'), {
    senderId: fromUserId,
    senderName: fromName,
    isAdmin: fromRole === 'admin',
    text,
    createdAt: serverTimestamp(),
  });
  await setDoc(reqRef, {
    lastMessageAt: serverTimestamp(),
    lastMessageText: text,
    lastMessageFromRole: fromRole,
    ...(fromRole === 'admin' ? { employeeUnread: true } : { adminUnread: true }),
  }, { merge: true });
};

export const markLeaveThreadSeen = (companyId, requestId, viewerRole) => {
  if (!companyId || !requestId) return Promise.resolve();
  return setDoc(
    doc(db, 'companies', companyId, 'leaveRequests', requestId),
    { [viewerRole === 'admin' ? 'adminUnread' : 'employeeUnread']: false },
    { merge: true }
  );
};

// ─── Secure Chat — identity keys ──────────────────────────────────────────────
// See CHAT_E2E_DESIGN.md §3 and §6, and crypto/chat.js for the actual
// cryptography. This section only ever stores/reads PUBLIC keys and
// PASSPHRASE-ENCRYPTED private-key backups — plaintext private keys never
// pass through this file or touch the network. One-shot getDoc/setDoc (not
// onSnapshot) is deliberate: these are checked once per session at chat-key
// setup/unlock time, not watched live like a thread or message list.

export const chatKeysCol = (companyId) => collection(db, 'companies', companyId, 'chatKeys');

// Returns { userId, publicKey, createdAt } or null if this user has never
// set up chat on any device.
export const getChatKey = async (companyId, userId) => {
  if (!companyId || !userId) return null;
  const snap = await getDoc(doc(db, 'companies', companyId, 'chatKeys', userId));
  return snap.exists() ? { userId, ...snap.data() } : null;
};

// First-use only in practice (a user's public key never needs to change once
// generated — there's no key-rotation flow in v1, see design doc §9), but
// implemented as a merge-write so re-running setup is harmless/idempotent.
export const setChatKey = (companyId, userId, publicKey) =>
  setDoc(doc(db, 'companies', companyId, 'chatKeys', userId), {
    publicKey,
    createdAt: serverTimestamp(),
  }, { merge: true });

export const chatKeyBackupsCol = (companyId) => collection(db, 'companies', companyId, 'chatKeyBackups');

// Returns { salt, nonce, encryptedPrivateKey, updatedAt } or null if this
// user has never completed setup (or somehow only ever unlocked on a device
// that already had a local copy, with no backup ever written — shouldn't
// happen via the modals in components/Chat, but guarded against here anyway).
export const getChatKeyBackup = async (companyId, userId) => {
  if (!companyId || !userId) return null;
  const snap = await getDoc(doc(db, 'companies', companyId, 'chatKeyBackups', userId));
  return snap.exists() ? snap.data() : null;
};

// Used both by initial setup and by a future "change my recovery passphrase"
// action (re-wrap the already-decrypted-in-memory private key under a new
// passphrase and overwrite here) — same write either way.
export const setChatKeyBackup = (companyId, userId, backup) =>
  setDoc(doc(db, 'companies', companyId, 'chatKeyBackups', userId), {
    ...backup,
    updatedAt: serverTimestamp(),
  }, { merge: true });

// ─── Secure Chat — threads & messages ──────────────────────────────────────────
// See CHAT_E2E_DESIGN.md §6. Thread docs hold only metadata (memberIds, name,
// timestamps) — never message content. Message docs hold ciphertext + a
// wrapped-key-per-member map; this file never sees plaintext and doesn't need
// to — it just persists whatever the crypto layer (crypto/chat.js, called
// from the chat UI components) hands it.

export const chatThreadsCol = (companyId) => collection(db, 'companies', companyId, 'chatThreads');

// Live list of every thread the signed-in user is a member of, newest
// activity first. Sorted client-side (not orderBy) so this doesn't need a
// composite index on (memberIds array-contains, lastMessageAt) — same
// reasoning as the existing WhatsApp message subscriptions in this file.
export const subscribeMyChatThreads = (companyId, userId, callback, onError = null) => {
  if (!companyId || !userId) return () => {};
  const q = query(chatThreadsCol(companyId), where('memberIds', 'array-contains', userId));
  return onSnapshot(
    q,
    snap => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => (b.lastMessageAt?.seconds || 0) - (a.lastMessageAt?.seconds || 0));
      callback(docs);
    },
    err => { console.error('[chatThreads]', err.code, err.message); if (onError) onError(err); }
  );
};

// Finds an existing 1:1 thread between exactly these two people, if any —
// used so "start a DM" with someone you've already messaged reopens the same
// thread instead of forking a duplicate one.
export const findDmThread = async (companyId, userIdA, userIdB) => {
  if (!companyId || !userIdA || !userIdB) return null;
  const q = query(chatThreadsCol(companyId), where('type', '==', 'dm'), where('memberIds', 'array-contains', userIdA));
  const snap = await getDocs(q);
  const match = snap.docs.find(d => {
    const memberIds = d.data().memberIds;
    return Array.isArray(memberIds) && memberIds.length === 2 && memberIds.includes(userIdB);
  });
  return match ? { id: match.id, ...match.data() } : null;
};

export const createDmThread = async (companyId, userIdA, userIdB) => {
  if (!companyId || !userIdA || !userIdB) throw new Error('createDmThread requires companyId and both user ids');
  const existing = await findDmThread(companyId, userIdA, userIdB);
  if (existing) return existing.id;
  const ref = await addDoc(chatThreadsCol(companyId), {
    type: 'dm',
    memberIds: [userIdA, userIdB],
    createdBy: userIdA,
    createdAt: serverTimestamp(),
    lastMessageAt: serverTimestamp(),
  });
  return ref.id;
};

export const createGroupThread = async (companyId, creatorId, memberIds, name) => {
  if (!companyId || !creatorId) throw new Error('createGroupThread requires companyId and creatorId');
  const allMembers = Array.from(new Set([creatorId, ...(memberIds || [])]));
  const ref = await addDoc(chatThreadsCol(companyId), {
    type: 'group',
    name: (name || '').trim() || 'New group',
    memberIds: allMembers,
    createdBy: creatorId,
    createdAt: serverTimestamp(),
    lastMessageAt: serverTimestamp(),
  });
  return ref.id;
};

export const updateChatThreadMembers = (companyId, threadId, memberIds) =>
  updateDoc(doc(db, 'companies', companyId, 'chatThreads', threadId), { memberIds });

export const updateChatThreadName = (companyId, threadId, name) =>
  updateDoc(doc(db, 'companies', companyId, 'chatThreads', threadId), { name: (name || '').trim() || 'New group' });

// Per-member last-read marker, stored as a map on the thread doc itself
// (`lastReadAt.{userId}`) rather than a separate collection — it's tiny,
// metadata-only (never message content, so the §7 hard-separation rule
// doesn't apply to it), and the existing chatThreads rule already allows any
// member to update any field on a thread they belong to.
export const markChatThreadRead = (companyId, threadId, userId) => {
  if (!companyId || !threadId || !userId) return Promise.resolve();
  return setDoc(
    doc(db, 'companies', companyId, 'chatThreads', threadId),
    { lastReadAt: { [userId]: serverTimestamp() } },
    { merge: true }
  );
};

export const chatMessagesCol = (companyId, threadId) =>
  collection(db, 'companies', companyId, 'chatThreads', threadId, 'messages');

export const subscribeChatMessages = (companyId, threadId, callback, onError = null) => {
  if (!companyId || !threadId) return () => {};
  const q = query(chatMessagesCol(companyId, threadId), orderBy('createdAt', 'asc'));
  return onSnapshot(
    q,
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    err => { console.error('[chatMessages]', err.code, err.message); if (onError) onError(err); }
  );
};

// Pure persistence — takes an already-encrypted message payload (ciphertext,
// nonce, wrappedKeys, etc., built by the not-yet-wired send pipeline in
// Task #149) and writes it, then bumps the thread's lastMessageAt. This file
// never constructs or inspects plaintext; it only stores what it's given.
export const addChatMessage = async (companyId, threadId, messageData) => {
  if (!companyId || !threadId) throw new Error('addChatMessage requires companyId and threadId');
  const ref = await addDoc(chatMessagesCol(companyId, threadId), {
    ...messageData,
    createdAt: serverTimestamp(),
  });
  await setDoc(
    doc(db, 'companies', companyId, 'chatThreads', threadId),
    { lastMessageAt: serverTimestamp(), lastMessageSenderId: messageData?.senderId || null },
    { merge: true }
  );
  return ref.id;
};

export const deleteChatMessage = (companyId, threadId, messageId) =>
  deleteDoc(doc(db, 'companies', companyId, 'chatThreads', threadId, 'messages', messageId));

// Generates a new message doc ID locally — no network round-trip, `doc()`
// without a write just allocates an ID client-side. File-attachment sends
// (Task #150, see components/Chat/encryptedAttachments.js) need the message
// ID BEFORE the message document is written, because the encrypted bytes are
// uploaded to Storage at .../chatFiles/{threadId}/{messageId} first (design
// doc §5) and only then is the message doc created pointing at that path.
export const newChatMessageId = (companyId, threadId) =>
  doc(chatMessagesCol(companyId, threadId)).id;

// Same persistence contract and crypto-agnosticism as addChatMessage above,
// but for a caller that already knows the message ID (i.e. file sends, which
// must mint the ID first as described above). Uses setDoc instead of addDoc
// since the ID is already chosen rather than server-generated.
export const setChatMessage = async (companyId, threadId, messageId, messageData) => {
  if (!companyId || !threadId || !messageId) throw new Error('setChatMessage requires companyId, threadId, and messageId');
  await setDoc(doc(db, 'companies', companyId, 'chatThreads', threadId, 'messages', messageId), {
    ...messageData,
    createdAt: serverTimestamp(),
  });
  await setDoc(
    doc(db, 'companies', companyId, 'chatThreads', threadId),
    { lastMessageAt: serverTimestamp(), lastMessageSenderId: messageData?.senderId || null },
    { merge: true }
  );
  return messageId;
};
